import { DocSectionType } from "../documentTypes";

/**
 * Defines a section type and its constraints
 */
export type SectionDefinition = {
    type: DocSectionType
    singleInDocument?: boolean
}

/**
 * Base sections available in all document types
 */
export const BASE_SECTION_DEFINITIONS: SectionDefinition[] = [
    { type: 'GROUP' },
    { type: 'APPROVAL_CARD', singleInDocument: true },
    { type: 'VERSIONS_HISTORY', singleInDocument: true },
    { type: 'ATTACHMENTS', singleInDocument: true },
    { type: 'DOC_REFERENCES', singleInDocument: true },
    // Not single: one per form definition, which the add-section dialog enforces.
    { type: 'FORM' },
]

/**
 * Validation-related sections (used by DQ, IQ, QQ, PQ document types)
 */
export const VALIDATION_SECTION_DEFINITIONS: SectionDefinition[] = [
    { type: 'TESTS', singleInDocument: true },
    { type: 'TESTS_TEAM', singleInDocument: true },
    { type: 'DEVIATIONS', singleInDocument: true },
    { type: 'VALIDATED_OBJECT', singleInDocument: true },
    { type: 'VALIDATION_TEAM', singleInDocument: true },
    { type: 'TESTS_SCHEDULE', singleInDocument: true },
    { type: 'TESTS_METHODOLOGY', singleInDocument: true },
    { type: 'RESPONSIBILITIES', singleInDocument: true },
    { type: 'QUESTIONS_WITH_LIST', singleInDocument: true },
]

/**
 * Document type-specific sections
 * Merged with BASE_SECTION_DEFINITIONS when building the final set
 */
export const DOCUMENT_TYPE_SECTION_DEFINITIONS: Record<string, SectionDefinition[]> = {
    URS: [{ type: 'REQUIREMENTS', singleInDocument: true }],
    RA: [{ type: 'RISK_ANALYSIS', singleInDocument: true }
        , { type: 'RA_MITIGATION_SCHEDULE', singleInDocument: true }
        , { type: 'RA_RECOMMENDED_ACTIONS', singleInDocument: true }
        , { type: 'RA_LIMITS', singleInDocument: true }
        , { type: 'RA_SUMMARY', singleInDocument: true }
        , { type: 'TESTS', singleInDocument: true }
    ],
    DQ: [...VALIDATION_SECTION_DEFINITIONS,
    { type: 'DQ_MAPPINGS', singleInDocument: true }],
    IQ: [...VALIDATION_SECTION_DEFINITIONS],
    OQ: [...VALIDATION_SECTION_DEFINITIONS],
    PQ: [...VALIDATION_SECTION_DEFINITIONS],
    AK: [{ type: 'QUESTIONS_WITH_LIST', singleInDocument: true }],
    AC: [
        { type: 'QUESTIONS_WITH_LIST', singleInDocument: true },
        { type: 'VALIDATION_TEAM', singleInDocument: true },
    ],
    ACS: [{ type: 'QUESTIONS_WITH_LIST', singleInDocument: true }],
    ACM: [{ type: 'QUESTIONS_WITH_LIST', singleInDocument: true }],
    ACF: [{ type: 'QUESTIONS_WITH_LIST', singleInDocument: true }],
    ACP: [{ type: 'QUESTIONS_WITH_LIST', singleInDocument: true }],
    VMP: [],
}

/**
 * Labels and l10n keys for all section types
 */
export const SECTION_TYPE_LABELS: Record<DocSectionType, { id: string, fallback: string }> = {
    GROUP: { id: 'doc-section-type-text', fallback: '_Grupa' },
    APPROVAL_CARD: { id: 'doc-section-type-approval-card', fallback: 'Karta zatwierdzeń' },
    VERSIONS_HISTORY: { id: 'doc-section-type-versions-history', fallback: 'Historia wersji' },
    RESPONSIBILITIES: { id: 'doc-section-type-responsibilities', fallback: 'Odpowiedzialności' },
    QUESTIONS_WITH_LIST: { id: 'doc-section-type-questions', fallback: 'Pytania z listą obiektów' },
    TESTS: { id: 'doc-section-type-tests', fallback: 'Testy' },
    TESTS_TEAM: { id: 'doc-section-type-tests-team', fallback: '_Zespół testowy' },
    DQ_MAPPINGS: { id: 'doc-section-type-dq-mappings', fallback: 'Mapowania DQ' },
    DEVIATIONS: { id: 'doc-section-type-deviations', fallback: '_Odchylenia' },
    RISK_ANALYSIS: { id: 'doc-section-type-risk-analysis', fallback: 'Analiza ryzyka' },
    VALIDATION_TEAM: { id: 'doc-section-type-validation-team', fallback: '_Zespol kwalifikacyjny' },
    TESTS_SCHEDULE: { id: 'doc-section-type-tests-schedule', fallback: '_Harmonogram testow' },
    TESTS_METHODOLOGY: { id: 'doc-section-type-tests-methodology', fallback: '_Metodologia testow' },
    VALIDATED_OBJECT: { id: 'doc-section-type-validated-object', fallback: '_Przedmiot walidacji' },
    ATTACHMENTS: { id: 'doc-section-type-attachments', fallback: '_Załączniki' },
    REQUIREMENTS: { id: 'doc-section-type-requirements', fallback: '_Wymagania' },
    RA_MITIGATION_SCHEDULE: { id: 'doc-section-type-ra_mitigation_schedule', fallback: '_Harmonogram' },
    RA_RECOMMENDED_ACTIONS: { id: 'doc-section-type-ra_recommended_actions', fallback: '_Zalecane działania' },
    RA_LIMITS: { id: 'doc-section-type-ra_limits', fallback: '_Limity' },
    DOC_REFERENCES: { id: 'doc-section-type-doc_references', fallback: '_Odwołania do dokumentów' },
    RA_SUMMARY: { id: 'doc-section-type-ra_summary', fallback: '_Podsumowanie analizy ryzyka' },
    FORM: { id: 'doc-section-type-form', fallback: '_Formularz' },
}

/**
 * Maps section type to its l10n localization key
 */
export function sectionTypeValueToL10Code(sectionType: DocSectionType): string {
    switch (sectionType) {
        case 'GROUP':
            return ''
        case 'APPROVAL_CARD':
            return 'doc-section-type-approval-card'
        case 'VERSIONS_HISTORY':
            return 'doc-section-type-versions-history'
        case 'RESPONSIBILITIES':
            return 'doc-section-type-responsibilities'
        case 'QUESTIONS_WITH_LIST':
            return 'doc-section-type-questions'
        case 'TESTS':
            return 'doc-section-type-tests'
        case 'TESTS_TEAM':
            return 'doc-section-type-tests-team'
        case 'DQ_MAPPINGS':
            return 'doc-section-type-dq-mappings'
        case 'DEVIATIONS':
            return 'doc-section-type-deviations'
        case 'RISK_ANALYSIS':
            return 'doc-section-type-risk-analysis'
        case 'VALIDATION_TEAM':
            return 'doc-section-type-validation-team'
        case 'TESTS_SCHEDULE':
            return 'doc-section-type-tests-schedule'
        case 'TESTS_METHODOLOGY':
            return 'doc-section-type-tests-methodology'
        case 'VALIDATED_OBJECT':
            return 'doc-section-type-validated-object'
        case 'ATTACHMENTS':
            return 'doc-section-type-attachments'
        case 'REQUIREMENTS':
            return 'doc-section-type-requirements'
        case 'RA_MITIGATION_SCHEDULE':
            return 'doc-section-type-ra_mitigation_schedule'
        case 'RA_RECOMMENDED_ACTIONS':
            return 'doc-section-type-ra_recommended_actions'
        case 'RA_LIMITS':
            return 'doc-section-type-ra_limits'
        case 'DOC_REFERENCES':
            return 'doc-section-type-doc_references'
        case 'RA_SUMMARY':
            return 'doc-section-type-ra_summary'
        case 'FORM':
            return 'doc-section-type-form'
    }
}

/**
 * Gets the merged and deduplicated section definitions for a specific document type.
 * Combines base sections with type-specific sections, respecting singleInDocument constraints.
 */
export function getSectionDefinitionsForDocumentType(documentType: string): SectionDefinition[] {
    const normalizedType = documentType.toUpperCase()
    const merged = [
        ...BASE_SECTION_DEFINITIONS,
        ...(DOCUMENT_TYPE_SECTION_DEFINITIONS[normalizedType] ?? []),
    ]

    const deduped = new Map<DocSectionType, SectionDefinition>()
    merged.forEach(section => {
        const existing = deduped.get(section.type)
        if (existing) {
            deduped.set(section.type, {
                type: section.type,
                singleInDocument: !!existing.singleInDocument || !!section.singleInDocument,
            })
            return
        }

        deduped.set(section.type, section)
    })

    return Array.from(deduped.values())
}

/**
 * What a section's report code may hold: the {{SectionCode:CODE}} a DOCX template uses to reach a GROUP
 * section. Mirrors SectionCodeRules on the API, which also checks the code is unique in the document.
 */
export const sanitizeSectionCode = (value: string) => value.toUpperCase().replace(/[^A-Z0-9_.-]/g, '').slice(0, 20)
